import type { ReactNode } from 'react'
import { cn } from '../cn'
import { statusLabel, statusTone, type Tone } from '../status'

export type BadgeTone = Tone | 'brand' | 'outline'

const TONE_CLASSES: Record<BadgeTone, string> = {
  positive: 'border-positive-line bg-positive-soft text-positive',
  attention: 'border-attention-line bg-attention-soft text-attention',
  watch: 'border-watch-line bg-watch-soft text-watch',
  quiet: 'border-quiet-line bg-quiet-soft text-quiet',
  info: 'border-info-line bg-info-soft text-info',
  brand: 'border-teal bg-teal text-white',
  outline: 'border-line-strong bg-surface text-ink-soft',
}

export interface BadgeProps {
  tone?: BadgeTone
  /** Leading dot. Status is never conveyed by colour alone, so the text always shows too. */
  dot?: boolean
  icon?: ReactNode
  className?: string
  children: ReactNode
}

export function Badge({ tone = 'quiet', dot = false, icon, className, children }: BadgeProps) {
  return (
    <span
      data-tone={tone}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-sm border px-2 py-0.5 text-xs font-semibold leading-5',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className="mt-[7px] size-1.5 shrink-0 self-start rounded-full bg-current"
        />
      ) : null}
      {icon ? (
        <span aria-hidden="true" className="mt-[3px] inline-flex self-start [&>svg]:size-3.5">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0">{children}</span>
    </span>
  )
}

export interface StatusBadgeProps {
  /** Any status value: finding, stage, workflow state, participation state, feed run… */
  status: string
  /** Override the display label (defaults to `statusLabel(status)`). */
  label?: string
  className?: string
}

/**
 * The one status component (STAT-007). Tone comes from the unified map in `status.ts`; the
 * label always shows next to a dot (STAT-008).
 */
export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  return (
    <Badge tone={statusTone(status)} dot className={className}>
      {label ?? statusLabel(status)}
    </Badge>
  )
}
